import { ApiError } from "./ApiError";
import { Action, VoidAction } from "../util/func";
import { z } from "zod";
import { statusResponseSchema } from "../schema/console/statusData";
import { GetCookieValue, SetCookie, DeleteCookie } from "../util/CookieManager";
import { tokenSchema } from "../schema/console/token";
import Time from "../util/Time";
import { setDeletePayload, setDeletePayloadSchema } from "../schema/general";
import { loginPayloadSchema } from "../schema/console/login";
import { getUserRowSchema } from "../schema/console/user";
import { GroupBaseSchema } from "../schema/generated/Group";
import { patchUserSchema, patchUserType, putUserSchema, putUserType, setNewPasswordPayload, setNewPasswordSchema } from "../schema/systemAdmin/user";
import { patchGroupSchema, patchGroupType, putGroupSchema, putGroupType } from "../schema/systemAdmin/group";

const ApiPath = process.env.REACT_APP_API_PATH;
type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type UserDataRequestPath = "/get-users" | "/put-user" | "/patch-user" | "/set-delete-user" | "/set-new-password-for-user";
type GroupDataRequestPath = "/get-groups" | "/put-group" | "/patch-group" | "/set-delete-group";
type OnlineRequestPath = UserDataRequestPath | GroupDataRequestPath | "/" | "/login" | "/check-token" | "/logout";

if (!ApiPath) {
  throw new Error("API_PATHが設定されていません。 package.json、.env.xxを確認してください。");
}

export class GuestApi {
  async Login(id: string, password: string, showError: Action<string>) {
    try {
      const response = await GuestApi.onlineRequest(loginPayloadSchema, "POST", "/login", { login_id: id, password });
      const loginData = await GuestApi.GetResponseData(response, tokenSchema);

      // アプリ対応のためcookieはこちらで設定する
      // オフライン作業中に認証期限切れが起きると問題ありそうなのでクライアント側で認証期限の管理は行わない
      SetCookie("app-auth-token", loginData.token, { maxAge: 365 * Time.ADay });
      return true;
    } catch {
      showError("ログインに失敗しました。");
      return false;
    }
  }

  protected static async onlineRequest<PayloadSchema extends z.ZodType>(payloadSchema: PayloadSchema, method: HttpMethod, path: OnlineRequestPath, data: z.infer<PayloadSchema>): Promise<Response> {
    const payload = payloadSchema.parse(data);
    console.log(`requested ${path}`);

    // アプリ用の認証対策としてCookieの代わりにヘッダーにTokenを含める。
    // TODO: セキュリティ的にどうかよくわからないので問題があればssl必須にはなるがクロスオリジン設定して対策する
    const headers: Record<string, string> = {};
    const token = GetCookieValue("app-auth-token");
    if (token) {
      headers["x-auth-token"] = token;
    }

    if (method === "GET" || method === "DELETE") {
      const payloadJson = JSON.stringify(payload);
      let query = "";
      if (payloadJson !== "{}") {
        query = `?data=${encodeURIComponent(payloadJson)}`;
      }
      const response = await fetch(`${ApiPath}${path}${query}`, { method, headers });
      return response;
    }

    headers["Content-Type"] = "application/json";
    const response = await fetch(`${ApiPath}${path}`, {
      method,
      headers,
      body: JSON.stringify(payload),
    });

    return response;
  }

  protected static requestEmpty(method: HttpMethod, path: OnlineRequestPath): Promise<Response> {
    return this.onlineRequest(z.object({}), method, path, {});
  }

  protected static async GetResponseData<ResSchema extends z.ZodType>(response: Response, resSchema: ResSchema): Promise<z.infer<ResSchema>> {
    const json = await response.json();
    if (response.ok && 200 <= response.status && response.status < 300) {
      return resSchema.parse(json);
    } else {
      throw new ApiError(response.status, json.error);
    }
  }
}

export class AuthedApi extends GuestApi {
  static async Get(setNull: VoidAction, showError: Action<string>) {
    // そもそも現状のままApiを使用できるかチェック
    let userToken: string | undefined;
    try {
      userToken = GetCookieValue("app-auth-token"); 
      if (!userToken) throw new Error("有効なCookieがありません。");
      if (!ApiPath) throw new Error("API_PATHが設定されていません。 package.json、.env.xxを確認してください。");
    } catch (e) {
      console.log(e);
      return null;
    }

    // サーバーオンラインチェック
    let isOnline : boolean;
    try {
      const statusResponse = await GuestApi.requestEmpty("GET", "/");
      const response = await GuestApi.GetResponseData(statusResponse, statusResponseSchema);
      isOnline = (response.status === "ok");
    } catch (e) {
      console.log(e);
      return null;
    }

    // ユーザーログインチェック
    try {
      const statusResponse = await GuestApi.requestEmpty("GET", "/check-token");
      await GuestApi.GetResponseData(statusResponse, statusResponseSchema);
    } catch (e) {
      console.log(e);
      return null;
    }
    return new AuthedApi(setNull, showError);
  }
  readonly setNull;
  readonly showError;
  
  constructor (setNull: VoidAction, showError: Action<string>) {
    super();
    this.showError = showError;
    this.setNull = setNull;

    if (!ApiPath) {
      throw new Error("API_PATHが設定されていません。 package.json、.env.xxを確認してください。");
    }
  }

  async Logout(userId: string) {
    // サーバ側のトークンも失効させる。失敗してもローカルのログアウトは続行する
    try {
      await GuestApi.requestEmpty("POST", "/logout");
    } catch (e) {
      console.log(e);
    }
    DeleteCookie("app-auth-token");
    this.setNull();
  }

  protected async request<PayloadSchema extends z.AnyZodObject, ResSchema extends z.ZodType>(payloadSchema: PayloadSchema, resSchema: ResSchema, method: HttpMethod, path: OnlineRequestPath, data: z.infer<PayloadSchema>): Promise<z.infer<ResSchema>> {
    const response = await GuestApi.onlineRequest(payloadSchema, method, path, data);

    if (response.ok && 200 <= response.status && response.status < 300) {
      const result = await GuestApi.GetResponseData(response, resSchema);
      return result;
    } else {
      const error = await response.json();
      this.showError(`リクエストエラー\n${error.error}`);
      throw new ApiError(response.status, error.error);
    }
  }

  public readonly user_data = {
    get: () => this.request(z.object({}), getUserRowSchema, "GET", "/get-users", {}),
    put: (payload: putUserType) => this.request(putUserSchema, statusResponseSchema, "PUT", "/put-user", payload),
    patch: (payload: patchUserType) => this.request(patchUserSchema, statusResponseSchema, "PATCH", "/patch-user", payload),
    setNewPassword: (payload: setNewPasswordPayload) => this.request(setNewPasswordSchema, statusResponseSchema, "PATCH", "/set-new-password-for-user", payload),
    delete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-user", payload),
  }

  public readonly group_data = {
    get: () => this.request(z.object({}), GroupBaseSchema.rows, "GET", "/get-groups", {}),
    put: (payload: putGroupType) => this.request(putGroupSchema, statusResponseSchema, "PUT", "/put-group", payload),
    patch: (payload: patchGroupType) => this.request(patchGroupSchema, statusResponseSchema, "PATCH", "/patch-group", payload),
    delete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-group", payload),
  }
}